import React from 'react';
import moment from 'moment';
import { Tooltip, Button } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import BaseMUIDataTable from '../../../../../components/BaseMUIDataTable/BaseMUIDataTable';
import { Colors } from '../../../../../assets/theme/Colors';
import { formatTime } from '../../../../../helpers/formatters';
import { getMuiTableDataIndex } from '../../../../../helpers/getters';
import {
  getRescuePickupLocationFullName,
  isRescueCancelled,
  isRescueClosed,
} from '../../../../../helpers/RescuesHelper';
import useHasActiveFeatureFlag from '../../../../../hooks/useFeatureFlags';
import { FF_DONOR_LINK } from '../../../../../components/FeatureFlagWrapper';
import DonorLinkPickupIndicator from '../../../../../components/DonorLinkPickupIndicator';
import { sortByDateAndPickupWindowStartAndEndTime, sortByPickupReady } from '../../../../../helpers/sorters';

const RescuerDashboardRecentRescuesTable = ({
  tableId,
  rescues,
  isLoading,
  onCloseRescueButtonClick,
  onCloseNoShowRescueButtonClick,
  onCellClick,
}) => {
  const hasActiveDonorLinkFF = useHasActiveFeatureFlag(FF_DONOR_LINK);

  const pickupReadyCustomBodyRender = (value, tableMeta) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue) {
      return null;
    }

    if (!rescue?.location_donor_link_active) {
      return '-';
    }

    return <DonorLinkPickupIndicator pickupReady={!!value} />;
  };

  return (
    <ThemeProvider
      theme={(theme) =>
        createTheme({
          ...theme,
          components: {
            ...theme.components,
            MUIDataTableHeadCell: {
              styleOverrides: {
                root: {
                  ...theme.components.MUIDataTableHeadCell.styleOverrides.root,
                  '&:last-child': {
                    width: 255,
                  },
                },
              },
            },
          },
        })
      }
    >
      <BaseMUIDataTable
        tableId={tableId}
        isLoading={isLoading}
        data={rescues}
        columns={[
          {
            name: 'id',
            label: 'ID',
            options: {
              filter: false,
              sort: false,
              display: false,
            },
          },
          {
            name: 'date',
            label: 'Date',
            options: {
              setCellProps: () => ({ sx: { width: 125 } }),
              sortCompare:
                (order) =>
                ({ rowData: row1 }, { rowData: row2 }) =>
                  sortByDateAndPickupWindowStartAndEndTime({
                    order,
                    firstDate: row1[1],
                    firstBegin: row1[3],
                    firstEnd: row1[4],
                    secondDate: row2[1],
                    secondBegin: row2[3],
                    secondEnd: row2[4],
                  }),
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                return (
                  <>
                    <span>
                      {moment(value, 'YYYYMMDD').format('MM/DD/YYYY')}
                    </span>

                    {rescue && rescue.cancelled_by_id && (
                      <>
                        <br />

                        <span style={{ color: Colors.errorText, fontWeight: 'bold' }}>
                          Cancelled
                        </span>
                      </>
                    )}
                  </>
                );
              },
            },
          },
          {
            name: 'day',
            label: 'Day',
            options: {
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRender: (value, tableMeta) =>
                moment(rescues[getMuiTableDataIndex(tableMeta)].date, 'YYYYMMDD').format('dddd'),
            },
          },
          {
            name: 'pickup_begin',
            label: 'Pickup Begin',
            options: {
              filter: false,
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRender: value => formatTime(value),
            },
          },
          {
            name: 'pickup_end',
            label: 'Pickup End',
            options: {
              filter: false,
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRender: value => formatTime(value),
            },
          },
          {
            name: 'location',
            label: 'Food Donor',
            options: {
              customFilterListOptions: {
                render: value => `Food Donor: ${value}`,
              },
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                if (!rescue) {
                  return null;
                }

                return getRescuePickupLocationFullName(rescue);
              },
            },
          },
          {
            name: 'receiver',
            label: 'Receiver',
            options: {
              customBodyRender: value => value || '',
            },
          },
          ...(hasActiveDonorLinkFF
            ? [
                {
                  name: 'rescue_confirmed',
                  label: 'Donor Link',
                  options: {
                    display: true,
                    filter: true,
                    customBodyRender: (value, tableMeta) => pickupReadyCustomBodyRender(value, tableMeta),
                    sortCompare: (order) => (row1, row2) => {
                      const rescue1 = rescues.find((fd) => fd.id === row1.rowData[0]);
                      const rescue2 = rescues.find((fd) => fd.id === row2.rowData[0]);

                      return sortByPickupReady(order, rescue1, rescue2);
                    },
                  },
                },
              ]
            : []),
          {
            name: 'slug',
            label: 'Description',
            options: {
              filter: false,
            },
          },
          {
            name: 'actions',
            label: 'Actions',
            options: {
              download: false,
              Empty: true,
              filter: false,
              searchable: false,
              viewColumns: false,
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                if (!rescue || isRescueClosed(rescue) || isRescueCancelled(rescue)) {
                  return false;
                }

                return (
                  <>
                    <Tooltip
                      placement="top"
                      key="Yes I did it!"
                      title="Close rescue"
                      sx={{ margin: '6px', fontSize: 11, padding: '6px' }}
                    >
                      <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => onCloseRescueButtonClick(rescue)}
                      >
                        Yes I did it!
                      </Button>
                    </Tooltip>

                    <Tooltip
                      placement="top"
                      key="I couldn't make it"
                      title="Close rescue"
                       sx={{ margin: '6px', fontSize: 11, padding: '6px' }}
                    >
                      <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => onCloseNoShowRescueButtonClick(rescue)}
                      >
                        I couldn't make it
                      </Button>
                    </Tooltip>
                  </>
                );
              },
            },
          },
        ]}
        options={{
          sortOrder: {
            name: 'date',
            direction: 'desc',
          },
          filter: false,
          download: false,
          search: false,
          viewColumns: false,
          pagination: false,
          rowsPerPage: 15,
          elevation: 0,
          selectableRows: 'none',
          responsive: 'simple',
          onCellClick: (colData, cellMeta) => {
            // ignore click on "actions" column cell
            if (cellMeta.colIndex >= 8) {
              return false;
            }

            const rescue = rescues[getMuiTableDataIndex(cellMeta)];

            if (!rescue) {
              return false;
            }

            return onCellClick(rescue);
          },
          setRowProps: (row, dataIndex) => {
            const rescue = rescues[dataIndex];

            if (rescue && isRescueCancelled(rescue)) {
              return {
                sx: {
                  '& > td': {
                    opacity: 0.25,
                  },
                },
              };
            }

            return false;
          },
        }}
      />
    </ThemeProvider>
  );
};

export default RescuerDashboardRecentRescuesTable;
